import { cur } from '@/lib/currency';
import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import PageTour from '@/components/PageTour';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Brain, Truck, DollarSign, Users, Megaphone, ArrowUp, ArrowDown, Minus, RefreshCw, AlertTriangle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import api from '@/lib/api';
import ShareViewButton from '@/components/common/ShareViewButton';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';

const RISK_COLORS = { churned: '#ef4444', high: '#f97316', medium: '#eab308', low: '#22c55e' };
const PIE_COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981'];

export default function AIHubPage() {
  const { t } = useLanguage();
  const [tab, setTab] = useState('suppliers');
  const [supplierData, setSupplierData] = useState(null);
  const [pricingData, setPricingData] = useState(null);
  const [churnData, setChurnData] = useState(null);
  const [marketingData, setMarketingData] = useState(null);
  const [loading, setLoading] = useState({ suppliers: false, pricing: false, churn: false, marketing: false });

  const fetch = (key, url, setter) => async () => {
    setLoading(p => ({ ...p, [key]: true }));
    try { setter((await api.get(url)).data); }
    catch { toast.error(t('ai_hub_load_error') || `Failed to load ${key} data`); }
    finally { setLoading(p => ({ ...p, [key]: false })); }
  };

  const fetchSuppliers = fetch('suppliers', '/v1/ai/supplier-recommendations', setSupplierData);
  const fetchPricing = fetch('pricing', '/v1/ai/pricing-optimization', setPricingData);
  const fetchChurn = fetch('churn', '/v1/ai/customer-churn', setChurnData);
  const fetchMarketing = fetch('marketing', '/v1/ai/marketing-optimization', setMarketingData);

  useEffect(() => { fetchSuppliers(); fetchPricing(); fetchChurn(); fetchMarketing(); }, []);

  const RefreshBtn = ({ onClick }) => (
    <Button size="sm" variant="outline" className="rounded-full text-xs h-7" onClick={onClick} data-testid="refresh-btn">
      <RefreshCw size={12} className="mr-1" />{t('refresh') || 'Refresh'}
    </Button>
  );

  const AiInsight = ({ text, testId }) => text ? (
    <Card className="border-purple-200 bg-purple-50/30">
      <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Brain size={16} className="text-purple-600" />{t('ai_insights') || 'AI Insights'}</CardTitle></CardHeader>
      <CardContent><div className="prose prose-sm max-w-none whitespace-pre-wrap" data-testid={testId}>{text}</div></CardContent>
    </Card>
  ) : null;

  const LoadingState = () => <div className="flex items-center justify-center h-48"><Loader2 className="animate-spin" size={32} /></div>;
  const EmptyState = ({ msg }) => <Card><CardContent className="py-12 text-center text-muted-foreground">{msg}</CardContent></Card>;

  const StatCard = ({ label, value, color, testId }) => (
    <Card className={`bg-gradient-to-br from-${color}-50 to-white border-${color}-200`}>
      <CardContent className="p-4">
        <p className="text-[10px] uppercase text-muted-foreground">{label}</p>
        <p className={`text-2xl font-bold text-${color}-700`} data-testid={testId}>{value}</p>
      </CardContent>
    </Card>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="ai-hub-page">
        <div>
          <div className="flex items-center gap-4">

            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-200/50">

              <Brain size={24} className="text-white" />

            </div>

            <div>

              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="page-title">AI Hub</h1>

              <p className="text-sm text-muted-foreground mt-0.5">AI-powered tools and insights hub</p>

            </div>

          </div>
          <div className="flex items-center gap-2">
            <PageTour storageKey="tour_aihub" steps={[
              { target: '[data-testid="tab-suppliers"]', title: 'Supplier Intelligence', content: 'AI analyzes your supplier data to find savings opportunities and reliability scores.' },
              { target: '[data-testid="tab-pricing"]', title: 'Pricing Optimization', content: 'Get AI-powered pricing suggestions to maximize revenue and margins.' },
              { target: '[data-testid="tab-churn"]', title: 'Churn Prediction', content: 'Identify customers at risk of leaving so you can take proactive action.' },
              { target: '[data-testid="tab-marketing"]', title: 'Marketing Insights', content: 'Find the best days and channels for promotions based on your sales data.' },
            ]} />
            <ShareViewButton params={{}} defaultBookmarkName="AI Hub" />
          </div>
          <p className="text-sm text-muted-foreground">{t('ai_hub_subtitle') || 'AI-powered business intelligence & optimization'}</p>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="space-y-4">
          <TabsList className="bg-muted flex-wrap h-auto gap-1">
            <TabsTrigger value="suppliers" className="data-[state=active]:bg-white" data-testid="tab-suppliers-2"><Truck size={14} className="mr-1" />{t('ai_supplier_intel') || 'Suppliers'}</TabsTrigger>
            <TabsTrigger value="pricing" className="data-[state=active]:bg-white" data-testid="tab-pricing-2"><DollarSign size={14} className="mr-1" />{t('ai_pricing_opt') || 'Pricing'}</TabsTrigger>
            <TabsTrigger value="churn" className="data-[state=active]:bg-white" data-testid="tab-churn-2"><Users size={14} className="mr-1" />{t('ai_churn') || 'Churn'}</TabsTrigger>
            <TabsTrigger value="marketing" className="data-[state=active]:bg-white" data-testid="tab-marketing-2"><Megaphone size={14} className="mr-1" />{t('ai_marketing') || 'Marketing'}</TabsTrigger>
          </TabsList>

          {/* SUPPLIER INTELLIGENCE */}
          <TabsContent value="suppliers">
            {loading.suppliers ? <LoadingState /> : !supplierData ? <EmptyState msg={t('no_data') || 'No supplier data'} /> : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <StatCard label={t('total_suppliers') || 'Total Suppliers'} value={supplierData.total_suppliers} color="blue" testId="total-suppliers" />
                  <StatCard label={t('total_spend') || 'Total Spend'} value={`${cur()} ${supplierData.total_spend?.toLocaleString()}`} color="emerald" testId="total-spend" />
                  <StatCard label={t('active') || 'Active'} value={supplierData.recommendations?.filter(s => s.active).length} color="purple" testId="active-suppliers" />
                </div>
                {supplierData.recommendations?.length > 0 && (
                  <Card>
                    <CardHeader><CardTitle className="text-sm">{t('ai_top_suppliers') || 'Top Suppliers by Spend'}</CardTitle></CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={supplierData.recommendations.slice(0, 8)} layout="vertical">
                          <XAxis type="number" tick={{ fontSize: 10 }} />
                          <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 10 }} />
                          <Tooltip formatter={v => `${cur()} ${v.toLocaleString()}`} />
                          <Bar dataKey="total_paid" name="Total Paid" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}
                <AiInsight text={supplierData.ai_analysis} testId="supplier-ai-analysis" />
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-sm">{t('ai_supplier_perf') || 'Supplier Performance'}</CardTitle>
                    <RefreshBtn onClick={fetchSuppliers} />
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs" data-testid="supplier-table">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-left p-2">{t('name') || 'Supplier'}</th>
                            <th className="text-right p-2">{t('ai_total_paid') || 'Total Paid'}</th>
                            <th className="text-right p-2">{t('orders') || 'Orders'}</th>
                            <th className="text-right p-2">{t('ai_avg_order') || 'Avg Order'}</th>
                            <th className="text-center p-2">{t('status') || 'Status'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {supplierData.recommendations?.map((s, i) => (
                            <tr key={i} className={i % 2 ? 'bg-muted/50' : ''}>
                              <td className="p-2 font-medium">{s.name}</td>
                              <td className="p-2 text-right font-bold">{cur()} {s.total_paid.toLocaleString()}</td>
                              <td className="p-2 text-right">{s.payment_count}</td>
                              <td className="p-2 text-right">{cur()} {s.avg_payment.toLocaleString()}</td>
                              <td className="p-2 text-center"><Badge variant={s.active ? 'default' : 'secondary'} className="text-[9px]">{s.active ? (t('active') || 'Active') : (t('inactive') || 'Inactive')}</Badge></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* PRICING OPTIMIZATION */}
          <TabsContent value="pricing">
            {loading.pricing ? <LoadingState /> : !pricingData ? <EmptyState msg={t('no_data') || 'No pricing data'} /> : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <StatCard label={t('ai_items_analyzed') || 'Items Analyzed'} value={pricingData.summary?.total_items} color="blue" testId="items-analyzed" />
                  <StatCard label={t('ai_avg_margin') || 'Avg Margin'} value={`${pricingData.summary?.avg_margin}%`} color="emerald" testId="avg-margin" />
                  <StatCard label={t('ai_price_increase') || 'Increase'} value={pricingData.summary?.items_to_increase} color="amber" testId="items-increase" />
                  <Card className={`bg-gradient-to-br ${(pricingData.summary?.potential_revenue_impact || 0) >= 0 ? 'from-purple-50 to-white border-purple-200' : 'from-red-50 to-white border-error/30'}`}>
                    <CardContent className="p-4">
                      <p className="text-[10px] uppercase text-muted-foreground">{t('ai_revenue_impact') || '30d Revenue Impact'}</p>
                      <p className={`text-xl font-bold ${(pricingData.summary?.potential_revenue_impact || 0) >= 0 ? 'text-purple-700' : 'text-error'}`} data-testid="revenue-impact">
                        {(pricingData.summary?.potential_revenue_impact || 0) >= 0 ? '+' : ''}SR {Math.abs(pricingData.summary?.potential_revenue_impact || 0).toLocaleString()}
                      </p>
                    </CardContent>
                  </Card>
                </div>
                <AiInsight text={pricingData.ai_analysis} testId="pricing-ai-analysis" />
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-sm">{t('ai_pricing_recs') || 'Pricing Recommendations'}</CardTitle>
                    <RefreshBtn onClick={fetchPricing} />
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs" data-testid="pricing-table">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-left p-2">{t('ai_item') || 'Item'}</th>
                            <th className="text-right p-2">{t('ai_current') || 'Current'}</th>
                            <th className="text-right p-2">{t('ai_cost') || 'Cost'}</th>
                            <th className="text-right p-2">{t('ai_margin') || 'Margin'}</th>
                            <th className="text-right p-2">{t('ai_sold_90d') || 'Sold (90d)'}</th>
                            <th className="text-right p-2">{t('ai_suggested') || 'Suggested'}</th>
                            <th className="text-center p-2">{t('ai_action') || 'Action'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pricingData.items?.map((item, i) => (
                            <tr key={i} className={i % 2 ? 'bg-muted/50' : ''}>
                              <td className="p-2 font-medium">{item.item_name}</td>
                              <td className="p-2 text-right">{cur()} {item.current_price}</td>
                              <td className="p-2 text-right text-muted-foreground">{item.cost_price > 0 ? `${cur()} ${item.cost_price}` : '-'}</td>
                              <td className={`p-2 text-right font-bold ${item.margin_pct < 20 ? 'text-error' : item.margin_pct < 40 ? 'text-warning' : 'text-success'}`}>{item.margin_pct}%</td>
                              <td className="p-2 text-right">{item.qty_sold_90d}</td>
                              <td className="p-2 text-right font-bold text-purple-600">{cur()} {item.suggested_price}</td>
                              <td className="p-2 text-center">
                                {item.price_change > 0 ? (
                                  <Badge className="text-[9px] bg-success/20 text-success"><ArrowUp size={10} className="mr-0.5" />+{item.price_change}</Badge>
                                ) : item.price_change < 0 ? (
                                  <Badge className="text-[9px] bg-error/20 text-error"><ArrowDown size={10} className="mr-0.5" />{item.price_change}</Badge>
                                ) : (
                                  <Badge variant="outline" className="text-[9px]"><Minus size={10} className="mr-0.5" />{t('ai_keep') || 'Keep'}</Badge>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* CUSTOMER CHURN */}
          <TabsContent value="churn">
            {loading.churn ? <LoadingState /> : !churnData ? <EmptyState msg={t('no_data') || 'No customer data'} /> : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <StatCard label={t('ai_total_customers') || 'Total Customers'} value={churnData.summary?.total_customers} color="blue" testId="total-customers" />
                  <StatCard label={t('ai_churned') || 'Churned'} value={churnData.summary?.churned} color="red" testId="churned-count" />
                  <StatCard label={t('ai_high_risk') || 'High Risk'} value={churnData.summary?.high_risk} color="amber" testId="high-risk-count" />
                  <StatCard label={t('ai_at_risk_revenue') || 'At-Risk Revenue'} value={`${cur()} ${(churnData.summary?.total_at_risk_revenue || 0).toLocaleString()}`} color="purple" testId="at-risk-revenue" />
                </div>
                {/* Risk distribution pie */}
                {churnData.summary && (
                  <Card>
                    <CardHeader><CardTitle className="text-sm">{t('ai_risk_distribution') || 'Risk Distribution'}</CardTitle></CardHeader>
                    <CardContent className="flex items-center justify-center">
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie data={[
                            { name: 'Churned', value: churnData.summary.churned || 0 },
                            { name: 'High Risk', value: churnData.summary.high_risk || 0 },
                            { name: 'Medium Risk', value: churnData.summary.medium_risk || 0 },
                            { name: 'Low Risk', value: churnData.summary.low_risk || 0 },
                          ].filter(d => d.value > 0)} cx="50%" cy="50%" outerRadius={70} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                            {[RISK_COLORS.churned, RISK_COLORS.high, RISK_COLORS.medium, RISK_COLORS.low].map((c, i) => <Cell key={i} fill={c} />)}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}
                <AiInsight text={churnData.ai_analysis} testId="churn-ai-analysis" />
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-sm">{t('ai_customer_risk') || 'Customer Risk Analysis'}</CardTitle>
                    <RefreshBtn onClick={fetchChurn} />
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs" data-testid="churn-table">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-left p-2">{t('name') || 'Customer'}</th>
                            <th className="text-right p-2">{t('ai_total_spent') || 'Total Spent'}</th>
                            <th className="text-right p-2">{t('ai_transactions') || 'Transactions'}</th>
                            <th className="text-right p-2">{t('ai_days_inactive') || 'Days Inactive'}</th>
                            <th className="text-center p-2">{t('ai_risk_level') || 'Risk'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {churnData.customers?.map((c, i) => (
                            <tr key={i} className={i % 2 ? 'bg-muted/50' : ''}>
                              <td className="p-2 font-medium">{c.name}</td>
                              <td className="p-2 text-right font-bold">{cur()} {c.total_spent.toLocaleString()}</td>
                              <td className="p-2 text-right">{c.transaction_count}</td>
                              <td className="p-2 text-right">{c.days_since_purchase < 999 ? c.days_since_purchase : '-'}</td>
                              <td className="p-2 text-center">
                                <Badge className="text-[9px]" style={{ backgroundColor: RISK_COLORS[c.risk] + '20', color: RISK_COLORS[c.risk] }}>
                                  {c.risk === 'churned' && <AlertTriangle size={10} className="mr-0.5" />}
                                  {c.risk.charAt(0).toUpperCase() + c.risk.slice(1)}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* MARKETING OPTIMIZATION */}
          <TabsContent value="marketing">
            {loading.marketing ? <LoadingState /> : !marketingData ? <EmptyState msg={t('no_data') || 'No marketing data'} /> : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <StatCard label={t('ai_total_customers') || 'Total Customers'} value={marketingData.summary?.total_customers} color="blue" testId="mkt-customers" />
                  <StatCard label={t('ai_revenue_90d') || 'Revenue (90d)'} value={`${cur()} ${(marketingData.summary?.total_revenue_90d || 0).toLocaleString()}`} color="emerald" testId="mkt-revenue" />
                  <StatCard label={t('ai_vip_customers') || 'VIP Customers'} value={marketingData.summary?.vip_customers} color="purple" testId="mkt-vip" />
                  <StatCard label={t('ai_avg_value') || 'Avg Customer Value'} value={`${cur()} ${(marketingData.summary?.avg_customer_value || 0).toLocaleString()}`} color="amber" testId="mkt-avg-value" />
                </div>
                {/* Day performance */}
                <div className="grid grid-cols-2 gap-4">
                  <Card className="border-success/30 bg-success/10">
                    <CardContent className="p-4">
                      <p className="text-[10px] uppercase text-muted-foreground">{t('ai_best_day') || 'Best Day'}</p>
                      <p className="text-lg font-bold text-success" data-testid="best-day">{marketingData.summary?.best_day}</p>
                      <p className="text-xs text-muted-foreground">{cur()} {(marketingData.summary?.best_day_revenue || 0).toLocaleString()}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-error/30 bg-error/10">
                    <CardContent className="p-4">
                      <p className="text-[10px] uppercase text-muted-foreground">{t('ai_worst_day') || 'Worst Day'}</p>
                      <p className="text-lg font-bold text-error" data-testid="worst-day">{marketingData.summary?.worst_day}</p>
                      <p className="text-xs text-muted-foreground">{cur()} {(marketingData.summary?.worst_day_revenue || 0).toLocaleString()}</p>
                    </CardContent>
                  </Card>
                </div>
                {/* Top products pie */}
                {marketingData.summary?.top_products?.length > 0 && (
                  <Card>
                    <CardHeader><CardTitle className="text-sm">{t('ai_top_products') || 'Top Products (90d)'}</CardTitle></CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie data={marketingData.summary.top_products} cx="50%" cy="50%" outerRadius={70} dataKey="sold" nameKey="name" label={({ name, sold }) => `${name}: ${sold}`}>
                            {marketingData.summary.top_products.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}
                <AiInsight text={marketingData.ai_analysis} testId="marketing-ai-analysis" />
                {/* Campaign suggestions */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-sm">{t('ai_campaign_suggestions') || 'Campaign Suggestions'}</CardTitle>
                    <RefreshBtn onClick={fetchMarketing} />
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {marketingData.campaigns?.map((c, i) => (
                      <div key={i} className="border rounded-lg p-3 hover:bg-muted/50 transition-colors" data-testid={`campaign-${i}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-sm">{c.name}</span>
                          <Badge className={`text-[9px] ${c.expected_impact === 'high' ? 'bg-success/20 text-success' : c.expected_impact === 'medium' ? 'bg-warning/20 text-warning' : 'bg-muted text-foreground'}`}>
                            {c.expected_impact} impact
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mb-1">{c.strategy}</p>
                        <div className="flex gap-3 text-[10px] text-muted-foreground">
                          <span>{t('ai_target') || 'Target'}: {c.target}</span>
                          <span>{t('ai_channel') || 'Channel'}: {c.channel}</span>
                          <span>{t('ai_audience') || 'Audience'}: {c.audience_size}</span>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
